import { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, CalendarCheck, CreditCard,
  Dumbbell, TrendingUp, UserCircle, LogOut, Zap, User, ChevronDown, RotateCw
} from "lucide-react";
import { Button } from "./ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const NAV = [
  { to: "/member/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/member/attendance", label: "Attendance", icon: CalendarCheck },
  { to: "/member/membership", label: "Membership", icon: CreditCard },
  { to: "/member/workouts", label: "Workouts", icon: Dumbbell },
  { to: "/member/progress", label: "Progress", icon: TrendingUp },
];

export default function MemberLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false); // mobile sidebar
  const [avatarMenu, setAvatarMenu] = useState(false); // logout dropdown
  const avatarRef = useRef(null);

  const member = JSON.parse(localStorage.getItem("member_data") || "{}");

  const rawPhoto = member?.photo || "";
  const photoUrl = rawPhoto
    ? rawPhoto.startsWith("http") ? rawPhoto : `${BACKEND_URL}${rawPhoto}`
    : null;

  // Org branding from member_data (set at login)
  const orgName = member?.orgName || member?.org_name || "My Gym";
  const rawLogo = member?.orgLogo || member?.org_logo || "";
  const orgLogo = rawLogo
    ? rawLogo.startsWith("http") ? rawLogo : `${BACKEND_URL}${rawLogo}`
    : null;

  // Close avatar dropdown when clicking outside
  useEffect(() => {
    if (!avatarMenu) return;
    const handler = (e) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target)) {
        setAvatarMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [avatarMenu]);

  const handleLogout = () => {
    const savedOrgSlug = member?.orgSlug || member?.org_slug || localStorage.getItem("org_slug");
    localStorage.removeItem("member_token");
    localStorage.removeItem("member_data");
    localStorage.removeItem("org_slug");
    window.location.href = savedOrgSlug ? `/org/${savedOrgSlug}/login` : "/";
  };

  const sidebarLinkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive
      ? "bg-primary text-primary-foreground"
      : "text-muted-foreground hover:bg-secondary hover:text-foreground"
    }`;

  // Reusable avatar bubble
  const AvatarBubble = ({ size = "w-9 h-9" }) => (
    <div className={`${size} rounded-full overflow-hidden shrink-0 bg-primary/20
      flex items-center justify-center border border-border`}>
      {photoUrl ? (
        <img
          src={photoUrl}
          alt={member?.name || "member"}
          className="w-full h-full object-cover"
          onError={e => { e.currentTarget.style.display = "none"; }}
        />
      ) : (
        <span className="text-primary font-bold text-sm select-none">
          {member?.name?.[0]?.toUpperCase() || <User className="h-4 w-4" />}
        </span>
      )}
    </div>
  );

  const SidebarContent = () => (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-border shrink-0">
        {orgLogo ? (
          <img src={orgLogo} alt={orgName} className="w-9 h-9 rounded-lg object-cover shrink-0" />
        ) : (
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Zap className="h-5 w-5 text-primary" />
          </div>
        )}
        <div className="min-w-0">
          <p className="text-sm font-black font-heading text-primary tracking-wider truncate">{orgName}</p>
          <p className="text-[10px] text-muted-foreground font-mono uppercase tracking-widest">Member Portal</p>
        </div>
      </div>

      {/* Member info */}
      <div className="px-4 py-4 border-b border-border shrink-0">
        <div className="flex items-center gap-3">
          <AvatarBubble />
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">{member?.name || "Member"}</p>
            <p className="text-[11px] text-muted-foreground truncate">{member?.phone || ""}</p>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto overscroll-contain">
        {NAV.map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} className={sidebarLinkClass} onClick={() => setOpen(false)}>
            <Icon className="h-4 w-4 shrink-0" />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* Logout & Refresh */}
      <div className="px-3 py-4 border-t border-border shrink-0 flex items-center gap-2">
        <Button
          variant="ghost"
          className="flex-1 justify-start gap-3 text-muted-foreground hover:text-destructive"
          onClick={handleLogout}
        >
          <LogOut className="h-4 w-4" /> Logout
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 text-muted-foreground hover:text-foreground rounded-lg transition-transform hover:rotate-180 duration-500"
          onClick={() => window.location.reload()}
          title="Refresh App"
          data-testid="refresh-button"
        >
          <RotateCw className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <div data-layout-root="true" className="h-[100dvh] w-full overflow-hidden bg-background flex">

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-60 border-r border-border bg-card shrink-0 h-full">
        <SidebarContent />
      </aside>

      {/* Mobile slide-over sidebar */}
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-card border-r border-border z-50 flex flex-col">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">

        {/* ── Mobile topbar ── */}
        <header className="lg:hidden flex items-center gap-3 px-4 h-14 border-b border-border bg-card shrink-0 z-20">
          {/* Org branding */}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {orgLogo ? (
              <img src={orgLogo} alt={orgName} className="w-7 h-7 rounded-md object-cover shrink-0" />
            ) : (
              <div className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                <Zap className="h-4 w-4 text-primary" />
              </div>
            )}
            <span className="font-heading font-bold text-primary tracking-wider text-sm truncate">
              {orgName}
            </span>
          </div>

          {/* Refresh button */}
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-muted-foreground hover:text-foreground rounded-lg transition-transform hover:rotate-180 duration-500"
            onClick={() => window.location.reload()}
            title="Refresh App"
            data-testid="mobile-refresh-button"
          >
            <RotateCw className="h-4 w-4" />
          </Button>

          {/* Avatar with logout dropdown */}
          <div className="relative" ref={avatarRef}>
            <button
              onClick={() => setAvatarMenu(o => !o)}
              className="flex items-center gap-1 rounded-full focus:outline-none
                focus:ring-2 focus:ring-primary/40 transition"
            >
              <AvatarBubble size="w-8 h-8" />
              <ChevronDown className={`h-3 w-3 text-muted-foreground transition-transform ${avatarMenu ? "rotate-180" : ""
                }`} />
            </button>

            {/* Dropdown */}
            {avatarMenu && (
              <div className="absolute right-0 top-full mt-2 w-52 rounded-xl border border-border
                bg-card shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2">
                {/* Member info header */}
                <div className="px-4 py-3 border-b border-border">
                  <p className="text-sm font-semibold truncate">{member?.name || "Member"}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{member?.phone || ""}</p>
                  <p className="text-[11px] text-muted-foreground truncate">Member ID: #{member?.memberNumber || ""}</p>
                </div>
                {/* Profile */}
                <NavLink
                  to="/member/profile"
                  onClick={() => setAvatarMenu(false)}
                  className="flex items-center gap-3 px-4 py-3 text-sm text-foreground hover:bg-secondary transition-colors border-b border-border"
                >
                  <UserCircle className="h-4 w-4 text-muted-foreground" /> Profile
                </NavLink>
                {/* Logout */}
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm
                    text-destructive hover:bg-destructive/10 transition-colors"
                >
                  <LogOut className="h-4 w-4" /> Logout
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Scrollable content */}
        <main className="flex-1 overflow-y-auto overscroll-contain p-4 lg:p-6
          pb-[calc(4.5rem+env(safe-area-inset-bottom,0px))] lg:pb-6">
          {children}
        </main>
      </div>

      {/* ── iOS-style bottom tab bar (mobile only) ── */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50
        bg-card/95 backdrop-blur-md border-t border-border pb-4">

        {/* Nav tabs */}
        <div className="flex items-stretch">
          {NAV.map(({ to, label, icon: Icon }) => {
            const active =
              location.pathname === to ||
              (to !== "/member/dashboard" && location.pathname.startsWith(to));
            return (
              <NavLink
                key={to}
                to={to}
                className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2
                  transition-colors min-w-0"
              >
                <div className={`relative flex items-center justify-center w-8 h-8 rounded-full transition-all ${active ? "bg-primary/10" : ""
                  }`}>
                  <Icon className={`h-5 w-5 transition-transform ${active ? "text-primary scale-110" : "text-muted-foreground"
                    }`} />
                  {active && (
                    <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary" />
                  )}
                </div>
                <span className={`text-[9px] font-medium tracking-wide truncate w-full text-center ${active ? "text-primary" : "text-muted-foreground"
                  }`}>{label}</span>
              </NavLink>
            );
          })}
        </div>

        {/* iPhone home indicator */}
        <div style={{ height: "env(safe-area-inset-bottom, 0px)" }} />
      </nav>
    </div>
  );
}
